import React, { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Html5QrcodeScanner, Html5QrcodeScannerState } from 'html5-qrcode';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Drawer from '@mui/material/Drawer';
import DialogContent from '@mui/material/DialogContent';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import InputAdornment from '@mui/material/InputAdornment';

import { ApiParticipant } from '../../contexts/HttpClientContext';
import { MaUserLookupResult, ScannerCheckInState } from './EventModalTypes';
import { useTranslation } from 'react-i18next';

import { BarcodeDetector, prepareZXingModule, ZXING_WASM_VERSION } from "barcode-detector/ponyfill";

// Initialize the wasm polyfill using unpkg CDN as recommended in the documentation
prepareZXingModule({
    overrides: {
        locateFile: (path: string, prefix: string) => {
            if (path.endsWith(".wasm")) {
                return `https://unpkg.com/zxing-wasm@${ZXING_WASM_VERSION}/dist/reader/${path}`;
            }
            return prefix + path;
        },
    },
});

// Polyfill the global BarcodeDetector if it doesn't exist
if (!("BarcodeDetector" in window)) {
    // @ts-ignore
    window.BarcodeDetector = BarcodeDetector;
}

const NativeScanner = ({ onScanResult }: { onScanResult: (decodedText: string) => void }) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);
    const requestRef = useRef<number>(0);
    const isScanningActive = useRef(true);

    useEffect(() => {
        let stream: MediaStream | null = null;
        let detector: any = null;

        const initScanner = async () => {
            try {
                // Initialize detector
                // @ts-ignore
                detector = new window.BarcodeDetector({ formats: ['qr_code'] });

                stream = await navigator.mediaDevices.getUserMedia({
                    video: { facingMode: 'environment' }
                });

                if (videoRef.current) {
                    videoRef.current.srcObject = stream;
                    videoRef.current.play().catch(err => console.error("Video play error:", err));
                }

                const scanFrame = async () => {
                    if (videoRef.current && videoRef.current.readyState === videoRef.current.HAVE_ENOUGH_DATA) {
                        if (isScanningActive.current) {
                            try {
                                // Draw frame to canvas for extraction
                                const canvas = canvasRef.current;
                                const video = videoRef.current;
                                if (canvas && video) {
                                    canvas.width = video.videoWidth;
                                    canvas.height = video.videoHeight;
                                    const ctx = canvas.getContext('2d', { willReadFrequently: true });
                                    if (ctx) {
                                        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                                        const barcodes = await detector.detect(canvas);
                                        if (barcodes && barcodes.length > 0) {
                                            const decodedText = barcodes[0].rawValue;
                                            isScanningActive.current = false;
                                            onScanResult(decodedText);
                                            // Pause scanning for 2 seconds
                                            setTimeout(() => {
                                                isScanningActive.current = true;
                                            }, 2000);
                                        }
                                    }
                                }
                            } catch (e) {
                                console.error("Detection error:", e);
                            }
                        }
                    }
                    requestRef.current = requestAnimationFrame(scanFrame);
                };

                requestRef.current = requestAnimationFrame(scanFrame);

            } catch (err: any) {
                console.error("Camera access denied or error:", err);
                setErrorMsg("Åtkomst till kameran nekades eller ett fel uppstod.");
            }
        };

        initScanner();

        return () => {
            if (requestRef.current) {
                cancelAnimationFrame(requestRef.current);
            }
            if (stream) {
                stream.getTracks().forEach(track => track.stop());
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Box sx={{ width: '100%', backgroundColor: '#000', borderRadius: '8px', position: 'relative', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', maxHeight: 240 }}>
            {errorMsg ? (
                <Alert severity="error">{errorMsg}</Alert>
            ) : (
                <>
                    <video
                        ref={videoRef}
                        playsInline
                        muted
                        style={{ display: 'block', width: '100%', height: '240px', objectFit: 'cover' }}
                    />
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '44%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: '220px',
                            height: '220px',
                            border: '4px solid rgba(255, 255, 255, 0.7)',
                            borderRadius: '12px',
                            boxShadow: '0 0 0 4000px rgba(0, 0, 0, 0.5)',
                            pointerEvents: 'none'
                        }}
                    />
                    <canvas ref={canvasRef} style={{ display: 'none' }} />
                </>
            )}
        </Box>
    );
};

export const QrCodeScanner = ({
    handleScannerCheckIn,
    handleLookupMaUser,
    handleSearchMaUsers,
    handleScannerCheckInPartner,
    scannerCheckInState,
    searchOpenRequest,
    isMaEvent }
    : {
        handleScannerCheckIn: (scannedText: string, checkOut: boolean) => Promise<any>,
        handleLookupMaUser: (membershipNumber: string) => Promise<MaUserLookupResult | null>,
        handleSearchMaUsers: (textSearch: string) => Promise<MaUserLookupResult[]>,
        handleScannerCheckInPartner: (partnerParticipant: ApiParticipant) => Promise<void>,
        scannerCheckInState: ScannerCheckInState,
        searchOpenRequest?: number,
        isMaEvent?: boolean
    }) => {
    const useNativeScanner = true;
    const html5QrcodeScannerRef = useRef<Html5QrcodeScanner>(null);
    const scannerIsOpenRef = useRef(true);
    const [scannedText, setScannedText] = useState("");
    const [manualMemberNumber, setManualMemberNumber] = useState("");
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [lookupLoading, setLookupLoading] = useState(false);
    const [lookupError, setLookupError] = useState<string | null>(null);
    const [lookupUser, setLookupUser] = useState<MaUserLookupResult | null>(null);
    const [resolvedMemberNumber, setResolvedMemberNumber] = useState("");
    const [searchDialogOpen, setSearchDialogOpen] = useState(false);
    const [searchConfirmDialogOpen, setSearchConfirmDialogOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchError, setSearchError] = useState<string | null>(null);
    const [searchResults, setSearchResults] = useState<MaUserLookupResult[]>([]);
    const [selectedSearchUser, setSelectedSearchUser] = useState<MaUserLookupResult | null>(null);
    const { t } = useTranslation();
    const previousSearchOpenRequestRef = useRef(searchOpenRequest);

    const formatMaText = (value?: string) => {
        if (!value) return "";

        return value
            .replace(/<br\s*\/?\s*>/gi, "\n")
            .replace(/<[^>]*>/g, "")
            .replace(/&nbsp;/gi, " ")
            .replace(/&amp;/gi, "&")
            .replace(/&lt;/gi, "<")
            .replace(/&gt;/gi, ">")
            .trim();
    };

    const normalizeMembershipNumber = (raw: string) => {
        const cleaned = raw.trim().toUpperCase();
        return cleaned.startsWith("M") ? cleaned.substring(1) : cleaned;
    };

    const getIsMember = (user: MaUserLookupResult | null): boolean | undefined => {
        if (!user || typeof user !== 'object') return undefined;
        const candidates = [user.isMember, user.member, user.membershipActive, user.activeMember, user.isActiveMember, user.memberStatus];
        for (const value of candidates) {
            if (typeof value === 'boolean') return value;
            if (typeof value === 'number') return value === 1;
            if (typeof value === 'string') {
                const normalized = value.toLowerCase();
                if (["yes", "true", "active", "member", "1"].includes(normalized)) return true;
                if (["no", "false", "inactive", "non-member", "0"].includes(normalized)) return false;
            }
        }
        return undefined;
    };

    const getUserName = (user: MaUserLookupResult | null) => {
        if (!user) return "-";
        return user.name || `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || "-";
    };

    const getUserMembershipNumber = (user: MaUserLookupResult | null) => {
        return normalizeMembershipNumber(user?.membershipNumber ?? resolvedMemberNumber);
    };

    const getUserCity = (user: MaUserLookupResult | null) => {
        return user?.addresses?.[0]?.city || "-";
    };

    const resetSearchState = () => {
        setSearchLoading(false);
        setSearchError(null);
        setSearchResults([]);
        setSelectedSearchUser(null);
    };

    const handleManualCheckIn = async () => {
        if (!manualMemberNumber.trim()) return;

        const normalized = normalizeMembershipNumber(manualMemberNumber);
        setLookupLoading(true);
        setLookupError(null);
        setLookupUser(null);
        setResolvedMemberNumber(normalized);
        setConfirmDialogOpen(true);

        try {
            const user = await handleLookupMaUser(normalized);
            if (!user) {
                setLookupError(`Ingen användare hittades med medlemsnummer ${normalized}.`);
                return;
            }
            setLookupUser(user);
        } catch (error: any) {
            console.error("MA user lookup failed", error?.response?.status, error?.response?.data ?? error);
            setLookupError("Kunde inte hämta användaruppgifter från MA.");
        } finally {
            setLookupLoading(false);
        }
    };

    const handleConfirmManualCheckIn = () => {
        if (!resolvedMemberNumber) return;

        setConfirmDialogOpen(false);
        setScannedText(resolvedMemberNumber);
        handleScannerCheckIn(resolvedMemberNumber, false).then((timeout) => {
            // Keep success feedback visible longer for manual flow so staff can read it.
            const delay = Math.max(typeof timeout === 'number' ? timeout : 2000, 6000);
            setTimeout(() => {
                setScannedText("");
                setManualMemberNumber("");
            }, delay);
        });
    }

    const handleCloseConfirmDialog = () => {
        if (lookupLoading) return;
        setConfirmDialogOpen(false);
        setLookupError(null);
        setLookupUser(null);
        setResolvedMemberNumber("");
    };

    const handleOpenSearchDialog = () => {
        resetSearchState();
        setSearchDialogOpen(true);
    };

    const handleCloseSearchDialog = () => {
        if (searchLoading) return;
        setSearchDialogOpen(false);
        setSearchConfirmDialogOpen(false);
        resetSearchState();
        setSearchQuery("");
    };

    const handleSearchUsers = async () => {
        const trimmed = searchQuery.trim();
        if (trimmed.length < 2) {
            setSearchError(t('scanner.search_min_length'));
            setSearchResults([]);
            return;
        }

        setSearchLoading(true);
        setSearchError(null);
        setSearchResults([]);

        try {
            const users = await handleSearchMaUsers(trimmed);
            setSearchResults(users);
            if (users.length === 0) {
                setSearchError(t('scanner.search_no_results', { query: trimmed }));
            }
        } catch (error) {
            console.error('MA user search failed', error);
            setSearchError(t('scanner.search_failed'));
        } finally {
            setSearchLoading(false);
        }
    };

    const handleSelectSearchUser = (user: MaUserLookupResult) => {
        setSelectedSearchUser(user);
        setSearchConfirmDialogOpen(true);
    };

    const handleCloseSearchConfirmDialog = () => {
        if (searchLoading) return;
        setSearchConfirmDialogOpen(false);
        setSelectedSearchUser(null);
    };

    const handleConfirmSearchCheckIn = () => {
        const membershipNumber = getUserMembershipNumber(selectedSearchUser);
        if (!membershipNumber) return;

        setSearchConfirmDialogOpen(false);
        setSearchDialogOpen(false);
        setScannedText(membershipNumber);
        handleScannerCheckIn(membershipNumber, false).then((timeout) => {
            const delay = Math.max(typeof timeout === 'number' ? timeout : 2000, 6000);
            setTimeout(() => {
                setScannedText("");
            }, delay);
        });
    };

    const handleScanResult = (decodedText: string) => {

        try {

            var context = new AudioContext();
            var oscillator = context.createOscillator();
            oscillator.type = "sine";
            oscillator.frequency.value = 800;
            oscillator.connect(context.destination);
            oscillator.start();
            // Beep for 100 milliseconds
            setTimeout(function () {
                oscillator.stop();
            }, 100);
        } catch (error) {
            console.error("Could not play sound", error);
        }

        console.log("scanned text", decodedText);
        setScannedText(decodedText);

        if (html5QrcodeScannerRef.current) {
            const scannerState = html5QrcodeScannerRef.current.getState();

            if (scannerState === Html5QrcodeScannerState.SCANNING) {
                html5QrcodeScannerRef.current.pause();
            }
        }
        handleScannerCheckIn(decodedText, false).then((timeout) => {
            const delay = typeof timeout === 'number' ? timeout : 2000;
            setTimeout(() => {
                setScannedText("");
                if (html5QrcodeScannerRef.current) {
                    if (html5QrcodeScannerRef.current.getState() === Html5QrcodeScannerState.PAUSED) {
                        html5QrcodeScannerRef.current.resume();
                    }
                }
            }, delay);
        });


    }


    useEffect(() => {
        if (!isMaEvent) {
            previousSearchOpenRequestRef.current = searchOpenRequest;
            return;
        }

        if (searchOpenRequest && searchOpenRequest !== previousSearchOpenRequestRef.current) {
            handleOpenSearchDialog();
        }

        previousSearchOpenRequestRef.current = searchOpenRequest;
    }, [handleOpenSearchDialog, isMaEvent, searchOpenRequest]);

    useEffect(() => {
        if (useNativeScanner) return;
        scannerIsOpenRef.current = true;

        if (!html5QrcodeScannerRef.current) {
            html5QrcodeScannerRef.current = new Html5QrcodeScanner(
                "scanner",
                //{ fps: 1, qrbox: qrboxFunction, aspectRatio: 1 },
                { fps: 10, qrbox: 220 },
                false
            );
            html5QrcodeScannerRef.current.render(
                (decodedText, _) => handleScanResult(decodedText),
                (errorMessage) => {
                    console.debug(`Error: ${errorMessage}`);
                }
            );
        }

        return () => {
            console.log("closing scanner component");
            scannerIsOpenRef.current = false;
            setScannedText("");

            if (html5QrcodeScannerRef.current) {
                const scannerState = html5QrcodeScannerRef.current.getState();
                console.log(scannerState);
                html5QrcodeScannerRef.current.clear().catch(async (error) => {
                    console.error(`Failed to clear scanner: ${error}`);
                });
                html5QrcodeScannerRef.current = null;
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [useNativeScanner]);

    return (
        <>
            {useNativeScanner ? (
                <NativeScanner onScanResult={handleScanResult} />
            ) : (
                <div id="scanner"></div>
            )}
            <Box sx={{ mt: 1, display: 'flex', gap: 1, alignItems: 'stretch' }}>
                <TextField
                    size="small"
                    placeholder="Medlemsnummer (t.ex. 12345)"
                    value={manualMemberNumber}
                    onChange={(e) => setManualMemberNumber(e.target.value)}
                    sx={{ flexGrow: 1, minWidth: 0 }}
                    slotProps={{
                        htmlInput: {
                            style: { textTransform: 'uppercase' }
                        }
                    }}
                />
                <Button
                    variant="contained"
                    onClick={handleManualCheckIn}
                    disabled={!manualMemberNumber.trim()}
                    sx={{ whiteSpace: 'nowrap', px: 2 }}
                >
                    Checka in
                </Button>
            </Box>
            <Dialog open={confirmDialogOpen} onClose={handleCloseConfirmDialog} fullWidth maxWidth="sm">
                <DialogTitle sx={{ m: 0, py: 1, pr: 7 }}>
                    Bekräfta incheckning
                    <IconButton
                        aria-label="close"
                        onClick={handleCloseConfirmDialog}
                        sx={(theme) => ({
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: theme.palette.grey[500],
                        })}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ pt: 2.5 }}>
                    {lookupLoading &&
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                            <CircularProgress size={28} />
                        </Box>
                    }

                    {!lookupLoading && lookupError &&
                        <Alert severity="warning" variant="outlined">{lookupError}</Alert>
                    }

                    {!lookupLoading && !lookupError && lookupUser &&
                        <Stack spacing={1.25}>
                            <Typography component="p"><b>{t('scanner.person_name_label')}</b> {getUserName(lookupUser)}</Typography>
                            <Typography component="p"><b>Medlemsnr:</b> {resolvedMemberNumber}</Typography>
                            <Typography component="p"><b>Adress:</b> {lookupUser.addresses?.[0]?.streetAddress || '-'}, {lookupUser.addresses?.[0]?.city || '-'}</Typography>
                            <Box>
                                {getIsMember(lookupUser) === true && <Chip color="success" label={t('scanner.member_chip')} />}
                                {getIsMember(lookupUser) === false && <Chip color="warning" label={t('scanner.non_member_chip')} sx={{ fontWeight: 700 }} />}
                                {getIsMember(lookupUser) === undefined && <Chip color="default" label={t('scanner.member_unknown_chip')} />}
                            </Box>
                            <Button variant="contained" onClick={handleConfirmManualCheckIn} sx={{ mt: 1 }}>
                                {t('scanner.confirm_check_in')}
                            </Button>
                        </Stack>
                    }
                </DialogContent>
            </Dialog>
            <Dialog
                open={searchDialogOpen}
                onClose={handleCloseSearchDialog}
                fullWidth
                maxWidth="sm"
                PaperProps={{
                    sx: {
                        m: 1,
                        mt: { xs: 1, sm: 3 },
                        alignSelf: { xs: 'flex-start', sm: 'center' },
                    }
                }}
            >
                <DialogTitle sx={{ m: 0, pr: 7, pb: 1 }}>
                    {t('scanner.search_users')}
                    <IconButton
                        aria-label="close"
                        onClick={handleCloseSearchDialog}
                        sx={(theme) => ({
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: theme.palette.grey[500],
                        })}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ pt: '20px !important' }}>
                    <Stack spacing={2}>
                        <TextField
                            autoFocus
                            size="small"
                            variant="outlined"
                            label={t('scanner.search_field_label')}
                            placeholder={t('scanner.search_field_placeholder')}
                            value={searchQuery}
                            onChange={(event) => setSearchQuery(event.target.value)}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                    void handleSearchUsers();
                                }
                            }}
                            slotProps={{
                                input: {
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            {!!searchQuery.trim() && (
                                                <IconButton
                                                    aria-label="clear-search"
                                                    onClick={() => {
                                                        setSearchQuery("");
                                                        setSearchError(null);
                                                        setSearchResults([]);
                                                    }}
                                                    edge="end"
                                                    size="small"
                                                >
                                                    <ClearIcon fontSize="small" />
                                                </IconButton>
                                            )}
                                            <IconButton aria-label={t('scanner.search_users')} onClick={() => void handleSearchUsers()} edge="end">
                                                <SearchIcon />
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }
                            }}
                        />
                        <Button variant="contained" onClick={() => void handleSearchUsers()} disabled={searchLoading || !searchQuery.trim()}>
                            {t('scanner.search_users')}
                        </Button>
                        {searchLoading &&
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                <CircularProgress size={28} />
                            </Box>
                        }
                        {!searchLoading && searchError &&
                            <Alert severity={searchResults.length === 0 ? 'info' : 'warning'} variant="outlined">{searchError}</Alert>
                        }
                        {!searchLoading && searchResults.length > 0 &&
                            <List sx={{ py: 0 }}>
                                {searchResults.map((user, index) => {
                                    const isMember = getIsMember(user);
                                    const membershipNumber = getUserMembershipNumber(user);
                                    return (
                                        <ListItemButton key={`${membershipNumber}-${index}`} onClick={() => handleSelectSearchUser(user)} divider>
                                            <ListItemText
                                                primary={
                                                    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                                                        <Typography component="span" sx={{ fontWeight: 600, minWidth: 0 }} noWrap>
                                                            {getUserName(user)}
                                                        </Typography>
                                                        {isMember === true && <Chip size="small" color="success" label={t('scanner.member_chip')} />}
                                                        {isMember === false && <Chip size="small" color="warning" label={t('scanner.non_member_chip')} sx={{ fontWeight: 700 }} />}
                                                    </Stack>
                                                }
                                                secondary={`${getUserCity(user)} • ${membershipNumber || '-'}`}
                                            />
                                        </ListItemButton>
                                    );
                                })}
                            </List>
                        }
                    </Stack>
                </DialogContent>
            </Dialog>
            <Dialog open={searchConfirmDialogOpen} onClose={handleCloseSearchConfirmDialog} fullWidth maxWidth="sm">
                <DialogTitle sx={{ m: 0, pr: 7 }}>
                    {t('scanner.confirm_search_title')}
                    <IconButton
                        aria-label="close"
                        onClick={handleCloseSearchConfirmDialog}
                        sx={(theme) => ({
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: theme.palette.grey[500],
                        })}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    {selectedSearchUser &&
                        <Stack spacing={1.25}>
                            <Typography component="p"><b>{t('scanner.person_name_label')}</b> {getUserName(selectedSearchUser)}</Typography>
                            <Typography component="p"><b>{t('scanner.person_membership_label')}</b> {getUserMembershipNumber(selectedSearchUser) || '-'}</Typography>
                            <Typography component="p"><b>{t('scanner.person_city_label')}</b> {getUserCity(selectedSearchUser)}</Typography>
                            <Box>
                                {getIsMember(selectedSearchUser) === true && <Chip color="success" label={t('scanner.member_chip')} />}
                                {getIsMember(selectedSearchUser) === false && <Chip color="warning" label={t('scanner.non_member_chip')} sx={{ fontWeight: 700 }} />}
                                {getIsMember(selectedSearchUser) === undefined && <Chip color="default" label={t('scanner.member_unknown_chip')} />}
                            </Box>
                            <Button variant="contained" onClick={handleConfirmSearchCheckIn} sx={{ mt: 1 }}>
                                {t('scanner.confirm_check_in')}
                            </Button>
                        </Stack>
                    }
                </DialogContent>
            </Dialog>
            {scannedText.length > 0 && (scannerCheckInState.status !== "idle" || scannerCheckInState.maResult) &&
                <Drawer
                    anchor='bottom'
                    open={scannedText.length > 0}
                    onClose={() => setScannedText("")}
                    sx={{ zIndex: 2000 }}
                >
                    <IconButton
                        aria-label="close"
                        onClick={() => setScannedText("")}
                        sx={(theme) => ({
                            position: 'absolute',
                            right: 18,
                            top: 14,
                            color: theme.palette.grey[500],
                        })}
                    >
                        <CloseIcon />
                    </IconButton>
                    <DialogContent>

                        <Box>
                            {scannerCheckInState.maResult ? (
                                <Box sx={{
                                    p: 2,
                                    backgroundColor: scannerCheckInState.maResult.fdBgColor || '#eee',
                                    borderRadius: '8px',
                                    color: '#000',
                                }}>
                                    <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
                                        {formatMaText(scannerCheckInState.maResult.fdHeading)}
                                    </Typography>
                                    <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                                        {formatMaText(scannerCheckInState.maResult.fdMessage)}
                                    </Typography>
                                    {scannerCheckInState.maResult.fdExtraInfo && (
                                        <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic', whiteSpace: 'pre-line' }}>
                                            {formatMaText(scannerCheckInState.maResult.fdExtraInfo)}
                                        </Typography>
                                    )}
                                    <Typography variant="caption" sx={{ mt: 1, display: 'block', opacity: 0.7 }}>
                                        Kod: {scannerCheckInState.maResult.code} (State: {scannerCheckInState.maResult.state})
                                    </Typography>
                                </Box>
                            ) : (
                                <>
                                    {scannerCheckInState.status === "success" &&
                                        <Alert severity="success" variant='outlined'>
                                            <AlertTitle>
                                                {t("scanner.status_checked_in")}
                                            </AlertTitle>
                                            <Typography component="p"><b>{scannerCheckInState?.booking?.name}</b> är nu incheckad.</Typography>
                                            <Typography component="p">Medlemsnummer: <b>{scannerCheckInState?.booking?.membershipNumber}</b></Typography>
                                            <Typography component="p">Bokningsinformation: {scannerCheckInState?.booking?.bookingInfo}</Typography>
                                            <Button onClick={() => handleScannerCheckIn(scannerCheckInState.booking!.membershipNumber, true)} variant='outlined' sx={{ marginTop: "10px" }}>{t("participants.check_out")}</Button>
                                        </Alert>
                                    }
                                    {scannerCheckInState.status === "alreadyCheckedIn" &&
                                        <Alert severity="info" variant='outlined'>
                                            <AlertTitle>
                                                {t("scanner.status_already_checked_in")}
                                            </AlertTitle>
                                            <Typography component="p"><b>{scannerCheckInState.booking!.name}</b> är redan incheckad.</Typography>
                                            <Typography component="p">Medlemsnummer: <b>{scannerCheckInState?.booking?.membershipNumber}</b></Typography>
                                            <Typography component="p">Bokningsinformation: {scannerCheckInState?.booking?.bookingInfo}</Typography>
                                            <Button onClick={() => handleScannerCheckIn(scannerCheckInState.booking!.membershipNumber, true)} variant='outlined' sx={{ marginTop: "10px" }}>{t("participants.check_out")}</Button>
                                        </Alert>
                                    }
                                    {scannerCheckInState.status === "notBooked" &&
                                        <Alert severity="warning" variant='outlined'>
                                            <AlertTitle>
                                                Bokning saknas!
                                            </AlertTitle>
                                            <Typography component="p">Ingen bokning hittades för {scannedText}</Typography>
                                        </Alert>
                                    }
                                    {scannerCheckInState.status === "invalidFormat" &&
                                        <Alert severity="warning" variant='outlined'>
                                            <AlertTitle>
                                                Felaktigt format!
                                            </AlertTitle>
                                            <Typography component="p">Det avlästa värdet "{scannedText}" har ett felaktigt eller okänt format.</Typography>
                                        </Alert>
                                    }
                                    {scannerCheckInState.status === "error" &&
                                        <Alert severity="error" variant='outlined'>
                                            <AlertTitle>
                                                {t("scanner.status_error")}
                                            </AlertTitle>
                                            <Typography component="p">Ett oväntat fel har inträffat.</Typography>
                                        </Alert>
                                    }
                                    {scannerCheckInState.status === "removed" &&
                                        <Alert severity="info" variant='outlined'>
                                            <AlertTitle>
                                                {t("participants.status_checked_out")}
                                            </AlertTitle>
                                            <Typography component="p"><b>{scannerCheckInState.booking!.name}</b> är nu utcheckad.</Typography>
                                            <Button onClick={() => handleScannerCheckIn(scannerCheckInState.booking!.membershipNumber, false)} variant='outlined' sx={{ marginTop: "10px" }}>{t("participants.check_in")}</Button>
                                        </Alert>
                                    }

                                    {(scannerCheckInState.status === "success-ask-partner"
                                        || scannerCheckInState.status === "partner-checkIn-loading")
                                        && scannerCheckInState.partnerParticipant &&
                                        <Alert severity="success" variant='outlined'>
                                            <AlertTitle>
                                                {t("scanner.status_checked_in")}
                                            </AlertTitle>
                                            <Typography component="p"><b>{scannerCheckInState?.booking?.name}</b> är nu incheckad.</Typography>
                                            <Typography component="p">Medlemsnummer: <b>{scannerCheckInState?.booking?.membershipNumber}</b></Typography>
                                            <Typography component="p">Bokningsinformation: {scannerCheckInState?.booking?.bookingInfo}</Typography>
                                            <Button onClick={() => handleScannerCheckIn(scannerCheckInState.booking!.membershipNumber, true)} variant='outlined' sx={{ marginTop: "10px", marginBottom: "10px" }}>{t("participants.check_out")}</Button>
                                            <Divider sx={{ marginBottom: "10px" }} />
                                            <Typography component="p">{t("scanner.status_ask_partner_desc", { name: scannerCheckInState.partnerParticipant?.name })}</Typography>
                                            <Typography component="p">Bokningsinformation: {scannerCheckInState.partnerParticipant?.bookingInfo}</Typography>
                                            {scannerCheckInState.status === "partner-checkIn-loading" &&
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent: 'center',
                                                        alignItems: 'center',
                                                    }}
                                                >
                                                    <CircularProgress size={'30px'} />
                                                </Box>
                                            }
                                            {scannerCheckInState.status === "success-ask-partner" &&
                                                <Button onClick={() => handleScannerCheckInPartner(scannerCheckInState.partnerParticipant!)} variant='outlined' sx={{ marginTop: "10px" }}>{t("scanner.yes_check_in_partner")}</Button>
                                            }
                                        </Alert>
                                    }

                                    {scannerCheckInState.status === "partner-success" && scannerCheckInState.partnerParticipant &&
                                        <Alert variant='outlined' severity='success'>
                                            <AlertTitle>{t("scanner.partner_checked_in")}</AlertTitle>
                                            <Typography component="p">{scannerCheckInState.partnerParticipant.name} är nu incheckad.</Typography>
                                            <Typography component="p">Bokningsinformation: {scannerCheckInState.partnerParticipant.bookingInfo} </Typography>
                                        </Alert>
                                    }

                                    {scannerCheckInState.status === "partner-alreadyCheckedIn" &&
                                        <Alert variant='outlined' severity='info'>{t("participants.status_partner_already_checked_in")}</Alert>
                                    }
                                    {scannerCheckInState.status === "partner-error" &&
                                        <Alert variant='outlined' severity='error'>
                                            <AlertTitle>{t("scanner.partner_error")}</AlertTitle>
                                            <Typography component="p">{t("scanner.partner_error_desc")}</Typography>
                                            {scannerCheckInState.partnerParticipant &&
                                                <Button onClick={() => handleScannerCheckInPartner(scannerCheckInState.partnerParticipant!)} variant='outlined'>{t("scanner.try_again")}</Button>
                                            }
                                        </Alert>
                                    }
                                </>
                            )}
                        </Box>
                    </DialogContent>
                </Drawer>
            }
        </>
    );
};
